import { useState } from "react";
import _, { isArray } from "lodash";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { callApi } from "../../api";
import { useAuth } from "../useAuth";

export default () => {
  const [dashboardData, setDashboardData] = useState(null);

  const [fetchPeriodData, setFetchPeriodData] = useState(false);

  const { selectedBusiness, selectedBusinessDetail, runWithGuard } = useAuth();
  const timezone = selectedBusinessDetail?.info?.timezone;
  const [loading, setLoading] = useState(null);

  const LOADING_TYPE = {
    UPDATE: "update",
    FETCH: "fetch",
    FETCH_BY_DAY: "fetch_by_day",
    ADD_CARD: "add_card",
    DELETE_CARD: "delete_card",
    DELETE_KPI: "delete_kpi",
    ADD_KPI: "add_kpi",
  };

  const fetchURL = (businessID) =>
    `/protected/business/${businessID}/dashboard`;

  const getFormattedData = (res) => {
    const formatted = { today: null, yesterday: null, lastmonth: null };
    if (res && res.status === true && res.data) {
      const isCurrentDay = (e) =>
        DateTime.fromISO(e).toISODate() ===
        DateTime.now()
          .setZone(timezone || undefined)
          .toISODate();
      const isYesterday = (e) =>
        DateTime.fromISO(e).toISODate() ===
        DateTime.now()
          .setZone(timezone || undefined)
          .plus({ days: -1 })
          .toISODate();
      const isEndOfLastMonth = (e) =>
        DateTime.fromISO(e).toISODate() ===
        DateTime.now()
          .setZone(timezone || undefined)
          .minus({ months: 1 })
          .endOf("month")
          .toISODate();

      if (Array.isArray(res.data) && !_.isEmpty(res.data)) {
        res.data.forEach((data) => {
          if (data.day && isCurrentDay(data.day)) formatted.today = data;
          if (data.day && isYesterday(data.day)) formatted.yesterday = data;
          if (data.day && isEndOfLastMonth(data.day))
            formatted.lastmonth = data;
        });
      } else if (!Array.isArray(res.data)) {
        if (res.data.day && isCurrentDay(res.data.day))
          formatted.today = res.data;
        if (res.data.day && isYesterday(res.data.day))
          formatted.yesterday = res.data;
        if (res.data.day && isEndOfLastMonth(res.data.day))
          formatted.lastmonth = res.data;
      }

      return formatted;
    }
    return null;
  };

  const fetchDataByDay = async (day) => {
    const res = await callApi(`${fetchURL(selectedBusiness.id)}?type=${day}`);
    if (res && res.status === true && res.data) {
      return res.data;
    }
    return null;
  };

  const fetchByDayAndCacheIfEmpty = async (day) => {
    if (!day) return;
    if (
      !dashboardData ||
      (dashboardData && !dashboardData[day]) ||
      _.isEmpty(dashboardData[day])
    ) {
      setLoading({
        type: LOADING_TYPE.FETCH_BY_DAY,
        state: true,
        day,
      });
      runWithGuard(async () => {
        const formattedData = await fetchDataByDay(day);
        if (!formattedData) {
          toast.error("Failed to fetch data!");
        } else if (isArray(formattedData)) {
          const formatted = getFormattedData({
            status: true,
            data: formattedData,
          });
          setDashboardData({
            ...dashboardData,
            today: formatted?.today || dashboardData?.today,
            yesterday: formatted?.yesterday || dashboardData?.yesterday,
            lastmonth: formatted?.lastmonth || dashboardData?.lastmonth,
          });
        } else {
          setDashboardData({ ...dashboardData, [day]: formattedData });
        }
        setLoading(null);
      });
    }
  };

  const getDashboardData = async () => {
    runWithGuard(async () => {
      setLoading({
        type: LOADING_TYPE.FETCH,
        state: true,
      });

      const res = await callApi(fetchURL(selectedBusiness.id));
      const formattedData = getFormattedData(res);
      if (!formattedData) {
        // toast.error(res?.message || "Failed to fetch data!");
      } else {
        setDashboardData({
          yesterday:
            formattedData.yesterday || formattedData.today
              ? formattedData.yesterday
              : await fetchDataByDay("yesterday"),
          lastmonth: formattedData.lastmonth,
          today: formattedData.today,
          // || (formattedData.yesterday && !formattedData.today)
          //   ? formattedData.today
          //   : await fetchDataByDay("today"),
          //  this ran report for today if dashboard returned null
        });
      }
      setLoading(null);
      setFetchPeriodData(true);
    });
  };

  const addOrUpdateCard = async ({ updateInCache, data }) => {
    const isEdit = !!data.id;
    runWithGuard(async () => {
      setLoading({
        type: isEdit ? LOADING_TYPE.UPDATE : LOADING_TYPE.ADD_CARD,
        state: true,
      });
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/pivotcolumn/dashboardcard/add`,
        "POST",
        {
          column: {
            ...(data || {}),
          },
        }
      );
      if (res && res.status === true) {
        if (updateInCache) updateInCache(res.data);
        toast.success(`${isEdit ? "Updated" : "Added"} successfully`);
      } else {
        toast.error(
          (res && res.message) || `Failed to ${isEdit ? "update" : "add"}!`
        );
      }
      setLoading(null);
    });
  };

  const addNewKPI = async ({ updateInCache, data }) => {
    runWithGuard(async () => {
      setLoading({
        type: LOADING_TYPE.ADD_KPI,
        state: true,
      });
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/pivotcolumn/dashboardkpi/add`,
        "POST",
        {
          column: {
            ...(data || {}),
          },
          global: false,
        }
      );
      if (res && res.status === true) {
        if (updateInCache) updateInCache(res.data);
        toast.success("Added successfully");
      } else {
        toast.error((res && res.message) || `Failed to add`);
      }
      setLoading(null);
    });
  };

  const deleteCardOrKPI = async (type, updateInCache, id) => {
    runWithGuard(async () => {
      setLoading({
        type:
          type === "card" ? LOADING_TYPE.DELETE_CARD : LOADING_TYPE.DELETE_KPI,
        state: true,
      });
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/pivotcolumn/dashboard${type}-column/delete/${id}`,
        "POST"
      );
      if (res && res.status === true) {
        if (updateInCache) updateInCache();
        toast.success(`Deleted successfully`);
      } else {
        toast.error((res && res.message) || `Failed to delete!`);
      }
      setLoading(null);
    });
  };

  const deleteCard = async ({ updateInCache, id }) => {
    deleteCardOrKPI("card", updateInCache, id);
  };

  const deleteKPI = async ({ updateInCache, id }) => {
    deleteCardOrKPI("kpi", updateInCache, id);
  };

  const fetchTodaysData = async () => {
    const formattedData = await fetchDataByDay("today");
    if (!formattedData) {
      toast.error("Failed to fetch data!");
    } else if (isArray(formattedData)) {
      const formatted = getFormattedData({
        status: true,
        data: formattedData,
      });
      setDashboardData({
        ...dashboardData,
        today: formatted?.today || dashboardData?.today,
        yesterday: formatted?.yesterday || dashboardData?.yesterday,
        lastmonth: formatted?.lastmonth || dashboardData?.lastmonth,
      });
    } else {
      setDashboardData({ ...dashboardData, today: formattedData });
    }
  };

  const resetDashboardData = () => {
    setDashboardData(null);
    setFetchPeriodData(false);
  };

  const getPeriodSnapshotSetting = async () => {
    const res = await callApi(
      `/protected/business/${selectedBusiness?.id}/iorequest?dest=periodsnapshotsetting/:BID:/get`,
      "GET"
    );
    return res;
  };

  const updatePeriodSnapshotSetting = async (setting) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness?.id}/iorequest?dest=periodsnapshotsetting/:BID:/update`,
      "POST",
      { data: setting }
    );
    return res;
  };

  return {
    todaysData: dashboardData?.today,
    yesterdaysData: dashboardData?.yesterday,
    thisMonthsData: dashboardData?.today?.monthtotal,
    lastMonthsData: dashboardData?.lastmonth?.monthtotal,
    fetchByDayAndCacheIfEmpty,
    addOrUpdateCard,
    deleteCard,
    deleteKPI,
    addNewKPI,
    loading,
    LOADING_TYPE,
    getDashboardData,
    fetchPeriodData,
    setFetchPeriodData,
    fetchTodaysData,
    resetDashboardData,
    getPeriodSnapshotSetting,
    updatePeriodSnapshotSetting,
  };
};
